import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AudioButton } from '../components/AudioButton';
import { MicrophoneButton } from '../components/MicrophoneButton';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { SRSReviewPanel, CAT_LABELS } from '../components/SRSReviewPanel';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, Loader2, Brain, Flame, BookOpen, Stethoscope, GraduationCap, Search } from 'lucide-react';
import '../styles/flashcards.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DICT_CATEGORIES = {
  general: { label: 'Allgemeines Deutsch', labelEn: 'General German', labelAr: 'الألمانية العامة', icon: GraduationCap, color: 'from-blue-600 to-indigo-700', badge: 'bg-blue-500', levels: [{ value: 'A1.1', label: 'Menschen A1.1 (L1-L12)' }, { value: 'A1.2', label: 'Menschen A1.2 (L13-L24)' }] },
  medical: { label: 'Medizinisches Deutsch', labelEn: 'Medical German', labelAr: 'الألمانية الطبية', icon: Stethoscope, color: 'from-emerald-600 to-teal-700', badge: 'bg-emerald-500', levels: [{ value: 'Pflege A1', label: 'Pflege A1 (Nursing)' }] }
};

export default function Flashcards() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [mode, setMode] = useState('dictionary');
  const [dictCategory, setDictCategory] = useState('general');
  const [dictLevel, setDictLevel] = useState('A1.1');
  const [dictCards, setDictCards] = useState([]);
  const [dictStats, setDictStats] = useState(null);
  const [dictLoading, setDictLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCat, setSelectedCat] = useState(null);
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [pronunciationScore, setPronunciationScore] = useState(null);
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0 });

  useEffect(() => {
    fetchDictStats();
    fetchDictCards('general', 'A1.1');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDictStats = async () => {
    try {
      const res = await axios.get(`${API}/flashcards/dictionary/stats`);
      setDictStats(res.data);
    } catch (err) { toast.error('Failed to load flashcard stats'); }
  };

  const fetchDictCards = async (cat, level) => {
    setDictLoading(true);
    try {
      const res = await axios.get(`${API}/flashcards/dictionary?category=${cat}&level=${encodeURIComponent(level)}`);
      setDictCards(res.data.cards || []);
    } catch (err) { toast.error('Failed to load flashcards'); }
    finally { setDictLoading(false); }
  };

  const handleCategoryChange = (cat) => {
    setDictCategory(cat);
    const firstLevel = DICT_CATEGORIES[cat].levels[0].value;
    setDictLevel(firstLevel);
    setSelectedCat(null);
    setSearchTerm('');
    fetchDictCards(cat, firstLevel);
  };

  const handleLevelChange = (lvl) => {
    setDictLevel(lvl);
    setSelectedCat(null);
    setSearchTerm('');
    fetchDictCards(dictCategory, lvl);
  };

  const fetchDueCards = async () => {
    setLoading(true);
    setCurrentIndex(0);
    setIsFlipped(false);
    setPronunciationScore(null);
    setSessionStats({ correct: 0, incorrect: 0 });
    try {
      const res = await axios.get(`${API}/flashcards/srs/due`);
      setCards(res.data.cards || []);
    } catch (error) {
      toast.error('Failed to load due cards');
    } finally {
      setLoading(false);
    }
  };

  const handleFlip = () => setIsFlipped(!isFlipped);

  const handleReview = async (quality) => {
    if (!currentCard) return;
    setReviewing(true);
    try {
      await axios.post(`${API}/flashcards/srs/review`, { card_id: currentCard.card_id, quality });
      setSessionStats(prev => ({
        correct: prev.correct + (quality >= 3 ? 1 : 0),
        incorrect: prev.incorrect + (quality < 3 ? 1 : 0)
      }));
      setCurrentIndex(prev => prev + 1);
      setIsFlipped(false);
      setPronunciationScore(null);
    } catch (error) {
      toast.error('Failed to save review');
    } finally {
      setReviewing(false);
    }
  };

  const getCardText = (card, side) => {
    if (!card) return '';
    if (side === 'back') {
      if (language === 'en') return card.front_en;
      if (language === 'ar') return card.front_ar;
      return card.front_en;
    }
    return card.front_de;
  };

  const filteredDictCards = dictCards.filter(c => {
    if (selectedCat && c.category !== selectedCat) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (c.front_de?.toLowerCase().includes(s) || c.front_en?.toLowerCase().includes(s) || c.front_ar?.includes(s));
  });

  const availableCats = [...new Set(dictCards.map(c => c.category))].sort();
  const currentCard = cards[currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="flashcards-page">
      <header className="bg-slate-900/50 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10" data-testid="back-to-dashboard">
            <ChevronLeft className="h-5 w-5 mr-1" /> Back
          </Button>
          <div className="flex bg-slate-800 rounded-lg p-1 gap-1" data-testid="mode-toggle">
            <button onClick={() => setMode('dictionary')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'dictionary' ? 'bg-teal-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`} data-testid="mode-dictionary">
              <BookOpen className="h-4 w-4" /> Wörterbuch
            </button>
            <button onClick={() => { setMode('srs'); if (cards.length === 0) fetchDueCards(); }} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'srs' ? 'bg-amber-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`} data-testid="mode-srs">
              <Brain className="h-4 w-4" /> SRS Review
            </button>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'srs' && <Badge className="bg-amber-500"><Flame className="h-4 w-4 mr-1" />{sessionStats.correct}</Badge>}
          </div>
        </div>
      </header>

      {mode === 'dictionary' && (
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 gap-4 mb-6" data-testid="dict-category-selector">
            {Object.entries(DICT_CATEGORIES).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const count = dictStats?.by_category?.[key] || 0;
              return (
                <button key={key} onClick={() => handleCategoryChange(key)} data-testid={`dict-cat-${key}`}
                  className={`relative overflow-hidden rounded-xl p-5 text-left transition-all ${dictCategory === key ? `bg-gradient-to-br ${cfg.color} shadow-xl scale-[1.02] ring-2 ring-white/20` : 'bg-slate-800/80 hover:bg-slate-800 border border-slate-700'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className={`h-6 w-6 ${dictCategory === key ? 'text-white' : 'text-slate-400'}`} />
                    <span className={`font-bold text-lg ${dictCategory === key ? 'text-white' : 'text-slate-200'}`}>{cfg.label}</span>
                  </div>
                  <p className={`text-sm ${dictCategory === key ? 'text-white/80' : 'text-slate-400'}`}>{count} Wörter · {cfg.labelEn}</p>
                </button>
              );
            })}
          </div>

          <div className="flex gap-2 mb-4" data-testid="dict-level-tabs">
            {DICT_CATEGORIES[dictCategory].levels.map(lv => (
              <button key={lv.value} onClick={() => handleLevelChange(lv.value)} data-testid={`dict-level-${lv.value}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${dictLevel === lv.value ? `${DICT_CATEGORIES[dictCategory].badge} text-white shadow-md` : 'bg-slate-800 text-slate-400 hover:text-white border border-slate-700'}`}>
                {lv.label}
              </button>
            ))}
          </div>

          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input type="text" placeholder="Suchen / Search / بحث ..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" data-testid="dict-search" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-5" data-testid="dict-cat-filters">
            <button onClick={() => setSelectedCat(null)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!selectedCat ? 'bg-teal-500 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-white'}`}>
              Alle ({filteredDictCards.length})
            </button>
            {availableCats.map(cat => (
              <button key={cat} onClick={() => setSelectedCat(cat === selectedCat ? null : cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedCat === cat ? 'bg-teal-500 text-white' : 'bg-slate-800 text-slate-400 border border-slate-700 hover:text-white'}`}>
                {CAT_LABELS[cat] || cat} ({dictCards.filter(c => c.category === cat).length})
              </button>
            ))}
          </div>

          {dictLoading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /><span className="ml-3 text-slate-400">Laden...</span></div>
          ) : filteredDictCards.length === 0 ? (
            <div className="text-center py-20 text-slate-400"><BookOpen className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>Keine Ergebnisse gefunden</p></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dict-cards-grid">
              {filteredDictCards.map((card) => (
                <div key={card.card_id} className="bg-slate-800/80 border border-slate-700/50 rounded-xl p-4 hover:border-teal-500/30 transition-all group" data-testid={`dict-card-${card.card_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-bold text-lg leading-tight">{card.front_de}</h3>
                    <span className="flex items-center gap-1 shrink-0 ml-2" onClick={(e) => e.stopPropagation()}>
                      <AudioButton text={card.audio_text || card.front_de} voice="nova" size="sm" />
                      <MicrophoneButton expectedText={card.front_de} size="sm" />
                    </span>
                  </div>
                  <p className="text-teal-400 text-sm mb-1">{card.front_en}</p>
                  <p className="text-amber-400/80 text-sm mb-2 font-arabic" dir="rtl">{card.front_ar}</p>
                  {card.back_example && (
                    <div className="bg-slate-900/50 rounded-lg p-2.5 mt-2 flex items-start gap-2">
                      <p className="text-slate-300 text-xs italic flex-1">"{card.back_example}"</p>
                      <span className="shrink-0" onClick={(e) => e.stopPropagation()}><AudioButton text={card.back_example} voice="nova" size="sm" /></span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="text-[10px] bg-slate-700 text-slate-300">{CAT_LABELS[card.category] || card.category}</Badge>
                    {card.lesson_label && <Badge className="text-[10px] bg-slate-700/50 text-slate-400">{card.lesson_label}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {mode === 'srs' && (
        <SRSReviewPanel
          cards={cards} currentIndex={currentIndex} currentCard={currentCard}
          isFlipped={isFlipped} loading={loading} sessionStats={sessionStats}
          pronunciationScore={pronunciationScore} reviewing={reviewing}
          onFlip={handleFlip} onReview={handleReview} onFetchDue={fetchDueCards}
          onSwitchToDictionary={() => setMode('dictionary')}
          onNavigateDashboard={() => navigate('/dashboard')}
          getCardText={getCardText} setPronunciationScore={setPronunciationScore}
        />
      )}
    </div>
  );
}
